import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import PasswordInput, { isPasswordValid } from '@/components/PasswordInput';
import { Lock, AlertTriangle } from 'lucide-react';

export default function ForcePasswordChange({ onSuccess }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate new password
    if (!isPasswordValid(newPassword)) {
      setError('Password must be at least 8 characters with uppercase, lowercase, and number');
      return;
    }

    // Check passwords match
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('Password updated successfully');
      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to change password';
      setError(detail);
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-white to-slate-50 p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-amber-100 rounded-full mb-4">
            <Lock className="w-12 h-12 text-amber-600" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight uppercase font-heading text-slate-900 mb-2">
            Password Change Required
          </h1>
          <p className="text-slate-600">
            You're using a temporary password. Please create a new secure password to continue.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg shadow-md p-8">
          {/* Warning banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">You cannot access the app until you set a new password.</p>
              <p className="mt-1 text-amber-700">Enter your temporary password below, then choose a new secure password.</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="currentPassword" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Temporary Password
              </Label>
              <PasswordInput
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter your temporary password"
                required
                data-testid="current-password-input"
              />
            </div>

            <div>
              <Label htmlFor="newPassword" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                New Password
              </Label>
              <PasswordInput
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Create a new password"
                showValidation={true}
                required
                data-testid="new-password-input"
              />
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-2 block">
                Confirm New Password
              </Label>
              <PasswordInput
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your new password"
                required
                data-testid="confirm-password-input"
              />
              {confirmPassword && newPassword !== confirmPassword && (
                <p className="text-red-500 text-xs mt-1">Passwords do not match</p>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={saving || !currentPassword || !newPassword || !confirmPassword}
              className="w-full h-12 bg-slate-900 hover:bg-slate-800 text-white font-bold uppercase tracking-wide"
              data-testid="change-password-submit"
            >
              {saving ? 'Updating...' : 'Set New Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
